import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import FileStates from 'FileStates';
import staticURL from 'lib/staticURL';
import {ChatSelectedFile} from 'llm/chat-library/ChatLibraryTypes';
import {openChat} from 'llm/chat-library/sidepanel/ChatRouterUtil';
import AlphaFoldTabState from 'molbio/aa-sequences/alphafold/AlphaFoldTabState';
import {ViewState} from 'molbio/aa-sequences/alphafold/AlphaFoldTabView';
import LoggedOutModal, {LOGGED_OUT_MODAL_KEY} from 'nav/LoggedOutModal';
import EntityStore from 'stores/EntityStore';
import {getIsPhoneWidth} from 'ui-library/hooks/useIsPhoneWidth';
import * as ModalContainer from 'ui-library/ModalContainer';
import * as ImportUtil from 'util/ImportUtil';
import {withLoadingScreen} from 'util/Promises';
import {assertNever} from 'util/Types';

import * as ChatActions from '../ChatActions';
import {getChatTypeForNewAgentChat} from '../sidepanel/ChatTypeUtil';
import {PromptSuggestion} from './PromptSuggestionTypes';

async function openAlphaFoldTab(proteinId: string): Promise<void> {
  const fileState = await FileStates.get().openFolderItem(proteinId);
  assert(fileState != null, 'Expected fileState to be defined', {fileId: proteinId});
  const alphaFoldTab = fileState.findTab(AlphaFoldTabState);
  if (!alphaFoldTab) {
    throw new Error(`Expected protein file to have an AlphaFoldTabState`);
  }
  // On mobile, we don't have the screen real estate to show a split workspace.
  if (getIsPhoneWidth()) {
    fileState.setIsWorkspaceSplit(false);
  }
  alphaFoldTab.set({active: true, initialView: ViewState.COORDINATE_STRUCTURES_LIST});
}

export default async function runPromptSuggestion(
  suggestion: PromptSuggestion,
  onProgress: (message: string) => void
): Promise<string | null> {
  switch (suggestion.action.type) {
    case 'NEW_CHAT': {
      const selectedFiles: Array<ChatSelectedFile> = [];
      const contextIds = [];
      switch (suggestion.action.input.type) {
        case 'demo-file': {
          onProgress('Finding file...');
          const path = `/static/llm/example-data/${suggestion.action.input.filename}`;
          const blob = await (await fetch(staticURL(path))).blob();
          const file = new File([blob], suggestion.action.input.filename, {type: blob.type});
          onProgress('Uploading file...');
          const s3Location = (await ImportUtil.upload(file))!;
          selectedFiles.push({
            key: _.uniqueId('file'),
            file,
            completedUpload: {s3Location, filename: file.name, mediaType: file.type},
          });
          break;
        }
        case 'benchling-objects': {
          contextIds.push(...suggestion.action.input.ids);
          break;
        }
        case 'none': {
          break;
        }
        default: {
          throw new Error(
            `Failed to run prompt suggestion. Unexpected input type: ${assertNever(suggestion.action.input).type}`
          );
        }
      }
      onProgress('Creating chat...');
      const chatType = getChatTypeForNewAgentChat(suggestion.action.agentType);
      const {chatSessionId} = await ChatActions.createChat(chatType, {}, suggestion.action.agentType);
      ChatActions.setDraftMessage(chatSessionId, {
        status: ChatActions.ChatDraftStatus.DRAFT,
        message: {
          text: suggestion.action.prompt,
          selectedFiles,
        },
        settings: {
          agentType: suggestion.action.agentType,
          publicDataEnabled: false,
        },
      });
      ChatActions.submitDraftMessage(chatSessionId, {
        createParams: {contextIds},
        analytics: {messageCount: 0},
      });
      openChat(chatSessionId);
      return chatSessionId;
    }
    case 'NEW_CHAT_PLACEHOLDER': {
      assert(
        EntityStore.currentUser() == null,
        'NEW_CHAT_PLACEHOLDER should only be shown to logged out users',
        {suggestion}
      );
      ModalContainer.open(
        LOGGED_OUT_MODAL_KEY,
        <LoggedOutModal
          onRequestClose={() => {
            ModalContainer.close(LOGGED_OUT_MODAL_KEY);
          }}
        />
      );
      return null;
    }
    case 'MODEL_HUB_OPEN_3D_STRUCTURE': {
      const {input} = suggestion.action;
      assert(input.type === 'benchling-objects', 'Expected benchling-objects input', {
        suggestion,
      });
      onProgress('Opening protein file...');
      // openFolderItem will unexpand the sidepanel, and on mobile that triggers AISidepanel to close itself,
      // so the onProgress above is not visible. To compensate, use a loading screen.
      const maybeLoadingScreen = getIsPhoneWidth() ? withLoadingScreen : <T,>(p: T) => p;
      await maybeLoadingScreen(openAlphaFoldTab(input.ids[0]), 'Opening protein file...');
      return null;
    }
    default:
      throw new Error(`Unknown action type: ${assertNever(suggestion.action).type}`);
  }
}
